import React, { useEffect, useState } from "react";
import { Loader2, Plus, Trash2, Users, KeyRound, Copy, History, Shield } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ROLES = [
  { v: "super_admin", l: "Super Admin", d: "Accès complet" },
  { v: "editor", l: "Éditeur", d: "Produits, sondage, médias" },
  { v: "analytics", l: "Analytics", d: "Consultation uniquement" },
];
const ROLE_L = { super_admin: "Super Admin", editor: "Éditeur", analytics: "Analytics" };

export default function AdminsManager() {
  const { authHeaders, user } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviting, setInviting] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "editor" });
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  const load = async () => {
    const [a, b] = await Promise.all([
      api.get("/admin/admins", { headers: authHeaders() }),
      api.get("/admin/audit", { params: { limit: 50 }, headers: authHeaders() }),
    ]);
    setAdmins(a.data); setAudit(b.data); setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const invite = async () => {
    setErr(""); setResult(null);
    if (!form.email) { setErr("Email requis"); return; }
    setInviting(true);
    try {
      const { data } = await api.post("/admin/admins", form, { headers: authHeaders() });
      setResult({ type: "invite", ...data });
      setForm({ email: "", name: "", role: "editor" });
      load();
    } catch (e) { setErr(e.response?.data?.detail || "Erreur"); }
    finally { setInviting(false); }
  };

  const reset = async (email) => {
    setErr(""); setResult(null);
    try { const { data } = await api.post(`/admin/admins/${encodeURIComponent(email)}/reset`, {}, { headers: authHeaders() }); setResult({ type: "reset", ...data }); }
    catch (e) { setErr(e.response?.data?.detail || "Erreur"); }
  };

  const del = async (email) => {
    if (!window.confirm(`Supprimer l'administrateur ${email} ?`)) return;
    try { await api.delete(`/admin/admins/${encodeURIComponent(email)}`, { headers: authHeaders() }); load(); }
    catch (e) { setErr(e.response?.data?.detail || "Erreur"); }
  };

  const copy = (t) => navigator.clipboard?.writeText(t);

  if (loading) return <div className="flex h-40 items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-caneat-orange" /></div>;

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2"><Users className="h-6 w-6 text-caneat-orange" /><h1 className="caneat-title text-3xl text-caneat-ink">Administrateurs</h1></div>

      <div className="rounded-2xl bg-white p-5 ring-1 ring-caneat-cream">
        <h2 className="caneat-title mb-3 text-lg text-caneat-ink">Inviter un membre</h2>
        <div className="grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
          <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="email@caneat.be" data-testid="invite-email"
            className="rounded-xl border-2 border-caneat-cream px-3 py-2 text-sm font-semibold outline-none focus:border-caneat-orange" />
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nom"
            className="rounded-xl border-2 border-caneat-cream px-3 py-2 text-sm outline-none focus:border-caneat-orange" />
          <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="invite-role"
            className="rounded-xl border-2 border-caneat-cream px-3 py-2 text-sm font-semibold outline-none focus:border-caneat-orange">
            {ROLES.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
          </select>
        </div>
        <p className="mt-2 text-xs text-caneat-muted">{ROLES.find((r) => r.v === form.role)?.d}</p>
        <button onClick={invite} disabled={inviting} data-testid="invite-submit"
          className="mt-3 flex items-center gap-2 rounded-full bg-caneat-orange px-4 py-2.5 text-sm font-bold text-white disabled:opacity-60">
          {inviting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Créer le compte</button>
        {err && <p className="mt-2 text-sm font-bold text-red-500">{err}</p>}

        {result && (
          <div className="mt-3 rounded-xl border-2 border-caneat-orange bg-caneat-orange/5 p-3" data-testid="invite-result">
            {result.type === "invite" ? (
              <>
                <p className="text-sm font-bold text-caneat-ink">Compte créé pour {result.email} ({ROLE_L[result.role]})</p>
                <p className="mt-1 text-xs text-caneat-muted">Mot de passe temporaire (à transmettre, modifiable ensuite) :</p>
                <div className="mt-1 flex items-center gap-2">
                  <code className="flex-1 rounded-lg bg-white px-3 py-2 text-sm font-bold text-caneat-ink ring-1 ring-caneat-cream">{result.temp_password}</code>
                  <button onClick={() => copy(result.temp_password)} className="flex h-9 w-9 items-center justify-center rounded-lg bg-caneat-orange text-white"><Copy className="h-4 w-4" /></button>
                </div>
              </>
            ) : (
              <>
                <p className="text-sm font-bold text-caneat-ink">Lien de réinitialisation pour {result.email}</p>
                <p className="mt-1 text-xs text-caneat-muted">Valable 24h, à transmettre au membre :</p>
                <div className="mt-1 flex items-center gap-2">
                  <code className="flex-1 truncate rounded-lg bg-white px-3 py-2 text-xs font-semibold text-caneat-ink ring-1 ring-caneat-cream">{result.reset_link}</code>
                  <button onClick={() => copy(result.reset_link)} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-caneat-orange text-white"><Copy className="h-4 w-4" /></button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <div className="rounded-2xl bg-white p-5 ring-1 ring-caneat-cream">
        <h2 className="caneat-title mb-3 text-lg text-caneat-ink">Membres de l'équipe</h2>
        <div className="space-y-2">
          {admins.map((a) => (
            <div key={a.email} className="flex items-center gap-3 rounded-xl bg-caneat-pageCream p-3" data-testid={`admin-row-${a.email}`}>
              <Shield className="h-5 w-5 shrink-0 text-caneat-orange" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-bold text-caneat-ink">{a.name} {a.email === user.email && <span className="text-xs text-caneat-muted">(vous)</span>}</p>
                <p className="truncate text-xs text-caneat-muted">{a.email} · {ROLE_L[a.role] || a.role}</p>
              </div>
              <button onClick={() => reset(a.email)} title="Réinitialiser" className="flex h-8 w-8 items-center justify-center rounded-lg bg-sky-50 text-sky-600"><KeyRound className="h-4 w-4" /></button>
              {a.email !== user.email && <button onClick={() => del(a.email)} data-testid={`del-admin-${a.email}`} className="flex h-8 w-8 items-center justify-center rounded-lg bg-red-50 text-red-500"><Trash2 className="h-4 w-4" /></button>}
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-2xl bg-white p-5 ring-1 ring-caneat-cream">
        <div className="mb-3 flex items-center gap-2"><History className="h-5 w-5 text-caneat-orange" /><h2 className="caneat-title text-lg text-caneat-ink">Historique des modifications</h2></div>
        {audit.length === 0 ? <p className="py-4 text-center text-sm text-caneat-muted">Aucune action enregistrée.</p> : (
          <ul className="divide-y divide-caneat-cream">
            {audit.map((a) => (
              <li key={a.id} className="flex items-start gap-3 py-2 text-sm">
                <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-caneat-orange" />
                <div><span className="font-semibold text-caneat-ink">{a.action}</span>{a.detail ? <span className="text-caneat-muted"> — {a.detail}</span> : null}
                  <p className="text-xs text-caneat-muted">{new Date(a.at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })} · {a.admin_email}</p></div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
